import { generateImageUrl } from '../utils/imageUtils';
import {getQuestionnaireConfigByMode} from '../domin/questionnaire/registry';

/**
 * 全局图片预加载服务
 * 用于在应用启动时预加载关键图片
 */
class ImagePreloadService {
    constructor() {
        this.preloadedImages = new Set();
        this.loadingPromises = new Map();
    }

    /**
     * 预加载单张图片
     * @param {string} src - 图片路径
     * @param {object} options - 图片处理选项
     * @returns {Promise} - 加载Promise
     */
    preloadImage(src, options = {}) {
        // Use processed URL as cache key to match actual rendering
        const processedUrl = generateImageUrl(src, options);
        const cacheKey = processedUrl;
        
        if (this.preloadedImages.has(cacheKey)) {
            return Promise.resolve(cacheKey);
        }

        if (this.loadingPromises.has(cacheKey)) {
            return this.loadingPromises.get(cacheKey);
        }

        const promise = new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => {
                this.preloadedImages.add(cacheKey);
                this.loadingPromises.delete(cacheKey);
                resolve(cacheKey);
            };
            img.onerror = () => {
                this.loadingPromises.delete(cacheKey);
                reject(new Error(`Failed to preload image: ${src}`));
            };
            img.src = processedUrl;
        });

        this.loadingPromises.set(cacheKey, promise);
        return promise;
    }

    /**
     * 批量预加载图片
     * @param {Array} imageList - 图片列表
     * @param {object} options - 图片处理选项
     * @returns {Promise} - 批量加载Promise
     */
    async preloadImages(imageList, options = {}) {
        const promises = imageList.map(src => this.preloadImage(src, options));
        return Promise.allSettled(promises);
    }

    /**
     * 获取问卷的关键图片列表
     * @param {object} questionnaireConfig - 问卷配置
     * @returns {Array<string>} 图片路径列表
     */
    getQuestionnaireCriticalImages(questionnaireConfig = {}) {
        return Array.isArray(questionnaireConfig.criticalImagePaths)
            ? questionnaireConfig.criticalImagePaths
            : [];
    }

    /**
     * 预加载指定问卷的关键图片
     * @param {string} questionnaireMode - 问卷 mode
     * @returns {Promise} - 预加载Promise
     */
    async preloadQuestionnaireImages(questionnaireMode) {
        if (!questionnaireMode) {
            return [];
        }

        const questionnaireConfig = getQuestionnaireConfigByMode(questionnaireMode);
        const criticalImages = this.getQuestionnaireCriticalImages(questionnaireConfig);

        return this.preloadImages(criticalImages, {
            quality: 100,
            formatSize: 1
        });
    }

    /**
     * 检查图片是否已预加载
     * @param {string} src - 图片路径
     * @param {object} options - 图片处理选项（用于生成相同的URL）
     * @returns {boolean} - 是否已加载
     */
    isImagePreloaded(src, options = {}) {
        const processedUrl = generateImageUrl(src, options);
        return this.preloadedImages.has(processedUrl);
    }

    /**
     * 清理预加载缓存
     */
    clearCache() {
        this.preloadedImages.clear();
        this.loadingPromises.clear();
    }
}

// 创建单例实例
export const imagePreloadService = new ImagePreloadService();
export default imagePreloadService;
